import React, {useMemo} from 'react'
import styled from 'styled-components'
import {IMAGE_ASSET, ROBOYU_COLOR_THEME, ROBOYU_MAX_LEVEL} from 'consts'
import {getTextShadow, getThemeColor} from 'utils'
import {useTheme} from 'themes'
import {useTranslation} from 'i18n'
import {Button, Icon, Modal, Paragraph, RoboyuDefault} from 'common/components'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'

interface ItemContainerProps {
  unlocked?: boolean
}
const StyledModal = styled(Modal)`
  width: 100%;
  max-width: ${convertUnit(335)};
  height: fit-content;
  text-align: center;
  display: flex;
  flex-direction: column;
  background: no-repeat center/cover
    url(${IMAGE_ASSET('giftshop', 'tree-square.png')});
  background-color: ${({theme}) => theme.primary_7};
  overflow: hidden;
`
const StyledLinearGradient = styled.div`
  height: 100%;
  ${({theme}) => ({
    background: `linear-gradient(180deg, #00000000 50%, ${getThemeColor(
      theme,
      'white_1',
      0.99,
    )} 100%) `,
  })}
`
const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  padding: ${convertUnit(20)};
  justify-content: space-between;
  align-items: center;
`
const StyledLevelContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  height: fit-content;
  justify-content: center;
  align-items: center;
`
const StyledLevel = styled.div`
  display: flex;
  position: absolute;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: fill-available;
`
const StyledParagraph = styled(Paragraph)`
  text-shadow: ${({theme}) =>
    getTextShadow(theme, {
      verticalOffset: 3,
      horizontalOffset: 4,
      color: 'fotoyu_logo_1',
      opacity: 0.99,
    })};
`
const StyledItemContainer = styled.div<ItemContainerProps>`
  ${({unlocked, theme}) => ({
    border: unlocked
      ? `${convertUnit(4)} solid ${theme.fotoyu_logo_1}`
      : 'unset',
    borderRadius: convertUnit(16),
    backgroundColor: theme.white_4,
  })}
  align-items: center;
  justify-content: center;
  display: flex;
  position: relative;
  width: fit-content;
  overflow: hidden;
  margin-top: ${convertUnit(20)};
`
const StyledOverlay = styled.div<ItemContainerProps>`
  background-color: ${({theme}) => getThemeColor(theme, 'black', 0.4)};
  position: absolute;
  width: fill-available;
  height: fill-available;
`
const StyledOverlayItem = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
`
const StyledButton = styled(Button)`
  display: flex;
  align-self: flex-end;
  width: fill-available;
  margin-top: ${convertUnit(20)};
`

interface ModalRoboyuLevelUpItemProps {
  toggleModal(): void
}

export default function ModalRoboyuLevelUpItem({
  toggleModal,
}: ModalRoboyuLevelUpItemProps) {
  const theme = useTheme()
  const {translate} = useTranslation()
  const {lv: level} = useSelector('roboyuState')

  const roboyuNearestLock = useMemo(
    () => ROBOYU_COLOR_THEME[Math.floor((level || 1) / 5) + 1],
    [level],
  )
  const roboyuRecentlyUnlocked = useMemo(
    () => ROBOYU_COLOR_THEME[Math.floor((level || 1) / 5)],
    [level],
  )
  const unlocked = useMemo(() => (level || 1) % 5 === 0, [level])
  const handleRenderItem = useMemo(
    () =>
      level > ROBOYU_MAX_LEVEL ? null : (
        <StyledItemContainer unlocked={unlocked}>
          <RoboyuDefault
            colorTheme={
              unlocked ? roboyuRecentlyUnlocked.name : roboyuNearestLock.name
            }
            width={convertUnit(100)}
            height={convertUnit(100)}
          />
          {unlocked ? null : (
            <>
              <StyledOverlay />
              <StyledOverlayItem>
                <Icon type="padlock" color="white_1" size={16} />
                <Paragraph color="white_1" fontSize="xs" fontWeight="bold">
                  {`LEVEL ${ROBOYU_COLOR_THEME.indexOf(roboyuNearestLock) * 5}`}
                </Paragraph>
              </StyledOverlayItem>
            </>
          )}
        </StyledItemContainer>
      ),
    [level, roboyuNearestLock, roboyuRecentlyUnlocked, unlocked],
  )

  return (
    <StyledModal visible onBackdropPress={toggleModal}>
      <StyledLinearGradient>
        <StyledContainer>
          <div>
            <StyledLevelContainer>
              <Icon
                type="star-level"
                size={100}
                color="white_1"
                hasShadow
                textShadow={getTextShadow(theme, {
                  verticalOffset: 3,
                  horizontalOffset: 4,
                  color: 'fotoyu_logo_1',
                  opacity: 0.99,
                })}
              />
              <StyledLevel>
                <Paragraph
                  fontSize="roboyu_level"
                  fontWeight="bold"
                  color="fotoyu_logo_1">
                  {level}
                </Paragraph>
              </StyledLevel>
            </StyledLevelContainer>
            <StyledParagraph
              fontSize="roboyu_level_up"
              fontWeight="bold"
              color="white_1">
              LEVEL UP
            </StyledParagraph>
          </div>
          {handleRenderItem}
          <StyledButton label={translate('global:ok')} onClick={toggleModal} />
        </StyledContainer>
      </StyledLinearGradient>
    </StyledModal>
  )
}
